// Build the URL that opens a provider's wall for one user. Template syntax:
//   {name}                 value from the context (user_id, user_ulid, click_id, username, country,
//                          email_hash, gaid) or from the wall's admin-set params
//   {gaid}                 the user's Google advertising ID from the Android app, or all zeros
//                          (00000000-0000-0000-0000-000000000000) when unknown, as the old site sent
//   {md5(...)}             md5 of the inner text after its own placeholders are filled
//   {sha256(...)}          same with sha256
//   {lower(...)}           the inner text lowercased (AdGem takes lowercase player ids only)
// Values are URL-encoded when inserted; hash outputs are hex.
//
// No database imports: the admin's wall form runs this same function for its preview
// (@gaintplay/core/wall), so what staff see is what users get.
import { sha256Hex } from '../crypto.js';
import { md5Hex } from './md5.js';
import { ZERO_GAID } from '../app-launch.js';

export interface WallContext {
  userId: string;
  clickId: string;
  username: string;
  country: string | null;
  emailHash?: string | null;
  /** The user's latest advertising ID from the Android app; zeros when unknown. */
  gaid?: string | null | undefined;
}

export class WallConfigError extends Error {}

/** The parts of a wall (WallConfig in @gaintplay/db) that make its link. */
export interface WallTemplate {
  urlTemplate: string;
  params?: Record<string, string> | undefined;
}

/** Placeholder names used inside {md5(...)} / {sha256(...)}: secrets, as a rule, never shown. */
export function hashedPlaceholders(template: string): string[] {
  const names = new Set<string>();
  for (const m of template.matchAll(/\{(?:md5|sha256)\(([^()]*)\)\}/gi))
    for (const n of m[1]!.matchAll(/\{([a-z0-9_]+)\}/gi)) names.add(n[1]!);
  return [...names];
}

/** A whole WallConfig can be passed; only urlTemplate and params make the link. */
export async function buildWallUrl<W extends WallTemplate>(
  wall: W,
  ctx: WallContext,
): Promise<string> {
  const values: Record<string, string> = {
    ...(wall.params ?? {}),
    user_id: ctx.userId,
    user_ulid: ctx.userId.replace(/^usr_/, ''),
    click_id: ctx.clickId,
    username: ctx.username,
    country: ctx.country ?? '',
    email_hash: ctx.emailHash ?? '',
    gaid: ctx.gaid || ZERO_GAID,
  };

  const fill = (text: string, encode: boolean): string =>
    text.replace(/\{([a-z0-9_]+)\}/gi, (_, name: string) => {
      if (!(name in values)) throw new WallConfigError(`unknown placeholder {${name}}`);
      const v = values[name]!;
      if (v === '') throw new WallConfigError(`wall param "${name}" is not set`);
      return encode ? encodeURIComponent(v) : v;
    });

  // Functions first (inner placeholders filled raw), then plain placeholders (URL-encoded).
  let out = wall.urlTemplate;
  const fnRe = /\{(md5|sha256|lower)\(([^()]*)\)\}/gi;
  const matches = [...out.matchAll(fnRe)];
  for (const m of matches) {
    const inner = fill(m[2]!, false);
    const fn = m[1]!.toLowerCase();
    const value =
      fn === 'lower'
        ? encodeURIComponent(inner.toLowerCase())
        : fn === 'md5'
          ? md5Hex(inner)
          : await sha256Hex(inner);
    out = out.replace(m[0], value);
  }
  out = fill(out, true);
  const url = new URL(out);
  if (url.protocol !== 'https:') throw new WallConfigError('wall URL must be https');
  return url.toString();
}
